import { MoreVert } from "@mui/icons-material";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { COLORS } from "assets";
import { MouseEvent, ReactNode, useCallback, useState } from "react";

export interface KebabMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

export interface KebabMenuProps {
  items: KebabMenuItem[];
  ariaLabel?: string;
  tooltip?: string;
}

export default function KebabMenu({ items, ariaLabel = "Actions", tooltip = "Actions" }: KebabMenuProps) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  const onOpen = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  }, []);

  const onClose = useCallback(() => setAnchor(null), []);

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          size="small"
          aria-label={ariaLabel}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={onOpen}
          sx={{
            color: COLORS.surface.secondaryText,
            borderRadius: `${COLORS.radius.sm}px`,
            "&:hover": { bgcolor: "rgba(30, 152, 217, 0.1)" }
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: `${COLORS.radius.sm}px`,
            boxShadow: COLORS.shadow.card,
            border: `1px solid ${COLORS.surface.border}`,
            minWidth: 160
          }
        }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.onClick();
            }}
            sx={{
              color: item.destructive ? COLORS.error : COLORS.surface.primaryText,
              fontSize: 14,
              py: 1
            }}
          >
            {item.icon ? (
              <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>{item.icon}</ListItemIcon>
            ) : null}
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
